/**
 * DeviceAssignDrawer — pick the devices assigned to a settings record: an environmental
 * control (Environmental settings) or a device careflow (Wellness settings).
 *
 * Merges two ~95%-identical copies (docs/DUPLICATION_AUDIT.md, B25). They differed only in
 * which field of the row holds the current device ids, the title and context line, the
 * save callback's shape, and which device fields show under the name and are searched.
 *
 * Search, Select All (adds the visible devices to the selection), Clear and Show Selected,
 * in the ws-device-picker styles.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(selectedIds: string[], initialIds: string[]) => void} onSave
 * @param {boolean} saving
 * @param {object|null} row — the record being edited; the selection resets when it changes
 * @param {string} idsKey — the row field holding the current ids as a CSV string
 * @param {string} title
 * @param {string} contextLabel — "Setting", "Careflow"
 * @param {string} [contextName] — shown after the label; '(unnamed)' when blank
 * @param {object[]} devices
 * @param {string[]} [detailFields] — shown under the name (in order) and searched, after
 *        device_name
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import Drawer from './Drawer.jsx';

const DeviceAssignDrawer = ({
    open, onClose, onSave, saving, row, idsKey, title, contextLabel, contextName, devices,
    detailFields = ['descriptions', 'entity_name', 'manufacture_uuid'],
}) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [initialIds, setInitialIds] = useState([]);
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    // Initialize selected IDs from the row when the drawer opens
    useEffect(() => {
        if (open && row) {
            const ids = row[idsKey]
                ? String(row[idsKey]).split(',').filter(Boolean)
                : [];
            setSelectedIds(ids);
            setInitialIds(ids);
            setSearch('');
            setShowSelectedOnly(false);
        }
    }, [open, row, idsKey]);

    // Filter and sort devices
    const filteredDevices = useMemo(() => {
        let list = [...(devices || [])];
        if (showSelectedOnly) {
            list = list.filter((d) => selectedIds.includes(String(d.device_id)));
        }
        if (search.trim()) {
            const term = search.toLowerCase();
            list = list.filter((d) => ['device_name', ...detailFields].some(
                (k) => String(d[k] || '').toLowerCase().includes(term),
            ));
        }
        list.sort((a, b) =>
            (a.device_name || '').localeCompare(b.device_name || '', undefined, { sensitivity: 'base' })
        );
        return list;
    }, [devices, search, showSelectedOnly, selectedIds, detailFields]);

    const handleToggle = useCallback((deviceId) => {
        const strId = String(deviceId);
        setSelectedIds((prev) =>
            prev.includes(strId) ? prev.filter((id) => id !== strId) : [...prev, strId]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        const allFilteredIds = filteredDevices.map((d) => String(d.device_id));
        setSelectedIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
    }, [filteredDevices]);

    const handleClear = useCallback(() => {
        setSelectedIds([]);
    }, []);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={title}
            footer={(
                <>
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={() => onSave(selectedIds, initialIds)}>
                        {saving ? 'Saving...' : 'Save Devices'}
                    </button>
                </>
            )}
        >
            {row ? (
                <p style={{ marginBottom: 'var(--space-3, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                    {contextLabel}: <strong>{contextName || '(unnamed)'}</strong>
                </p>
            ) : null}

            {/* Picker */}
            <div className="ws-device-picker">
                <div className="ws-device-picker__header">
                    <p className="dp-drawer__label" style={{ marginBottom: 0 }}>
                        Devices
                        <span className="ws-device-picker__count">
                            ({selectedIds.length} selected)
                        </span>
                    </p>
                </div>

                {/* Search */}
                <div className="ws-device-picker__search">
                    <i className="fe fe-search ws-device-picker__search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        placeholder="Search devices..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="ws-device-picker__search-input"
                    />
                </div>

                {/* Select All / Clear / Show Selected */}
                <div className="ws-device-picker__actions">
                    <button type="button" onClick={handleSelectAll} className="ws-device-picker__action-btn">
                        Select All
                    </button>
                    <button type="button" onClick={handleClear} className="ws-device-picker__action-btn">
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowSelectedOnly((prev) => !prev)}
                        className={`ws-device-picker__action-btn${showSelectedOnly ? ' ws-device-picker__action-btn--active' : ''}`}
                    >
                        Show Selected
                    </button>
                </div>

                {/* Device list */}
                <div className="ws-device-picker__list">
                    {filteredDevices.length === 0 ? (
                        <p className="ws-device-picker__empty">
                            {showSelectedOnly ? 'No selections.' : 'No devices found'}
                        </p>
                    ) : (
                        filteredDevices.map((d) => {
                            const dId = String(d.device_id);
                            const isChecked = selectedIds.includes(dId);
                            return (
                                <label key={dId} className={`ws-device-picker__item${isChecked ? ' is-selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleToggle(d.device_id)}
                                        className="ap-checkbox"
                                    />
                                    <div className="ws-device-picker__item-info">
                                        <span className="ws-device-picker__item-name">{d.device_name || '—'}</span>
                                        <span className="ws-device-picker__item-desc">
                                            {detailFields.map((k) => d[k]).filter(Boolean).join(' · ')}
                                        </span>
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>
            </div>
        </Drawer>
    );
};

export default DeviceAssignDrawer;
